
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { teamService } from '@/services/teamService';
import { Team } from '@/types/database';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, Calendar, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { TeamManagementView } from '@/components/teams/TeamManagementView';
import { PublicTeamView } from '@/components/teams/PublicTeamView';
import { TeamFollowButton } from '@/components/teams/TeamFollowButton';
import { FeaturedSpotlight } from '@/components/featured/FeaturedSpotlight';
import { SocialMediaEmbeds } from '@/components/social/SocialMediaEmbeds';

const TeamProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);

  const currentUser = supabaseUser || internalUser;
  const currentUserId = currentUser?.id;
  const userType = internalUser ? 'internal' : 'supabase';

  // Check if current user can manage this team (is creator or coach)
  const canManageTeam = () => {
    if (!currentUserId || !team) return false;
    return team.created_by === currentUserId;
  };

  useEffect(() => {
    const loadTeam = async () => {
      if (!slug) {
        console.error('No slug provided');
        setLoading(false);
        return;
      }
      
      try {
        console.log('Loading team with slug:', slug);
        setLoading(true);
        
        // Get all teams and find the one with matching slug
        const teams = await teamService.getTeams();
        console.log('All teams loaded:', teams);
        
        const foundTeam = teams.find(t => {
          console.log('Comparing team slug:', t.slug, 'with:', slug);
          return t.slug === slug;
        });
        
        if (foundTeam) {
          console.log('Team found:', foundTeam);
          setTeam(foundTeam);
        } else {
          console.log('Team not found with slug:', slug);
          toast.error('Team not found');
        }
      } catch (error) {
        console.error('Error loading team:', error);
        toast.error('Failed to load team');
      } finally {
        setLoading(false);
      }
    };

    loadTeam();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col">
        {/* Desktop Header */}
        <div className="hidden md:block">
          <Header />
        </div>
        {/* Mobile Header */}
        <div className="block md:hidden">
          <MobileHeader />
        </div>
        
        <main className="flex-grow pt-20 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-48 bg-gray-800 rounded-lg mb-6"></div>
              <div className="h-8 bg-gray-800 rounded mb-4"></div>
              <div className="h-4 bg-gray-800 rounded mb-2"></div>
              <div className="h-4 bg-gray-800 rounded mb-2"></div>
            </div>
          </div>
        </main>
        
        <div className="block md:hidden">
          <MobileBottomNav />
        </div>
        <Footer />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col">
        {/* Desktop Header */}
        <div className="hidden md:block">
          <Header />
        </div>
        {/* Mobile Header */}
        <div className="block md:hidden">
          <MobileHeader />
        </div>
        
        <main className="flex-grow pt-20 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold mb-4">Team Not Found</h1>
            <p className="text-gray-400">The team you're looking for doesn't exist or the URL may be incorrect.</p>
            <p className="text-gray-500 text-sm mt-2">Slug: {slug}</p>
          </div>
        </main>
        
        <div className="block md:hidden">
          <MobileBottomNav />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Desktop Header */}
      <div className="hidden md:block">
        <Header />
      </div>
      {/* Mobile Header */}
      <div className="block md:hidden">
        <MobileHeader />
      </div>
      
      <main className="flex-grow pt-20 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6">
        <div className="max-w-4xl mx-auto">
          {/* Team Header */}
          <div className="relative mb-8">
            <div 
              className="h-48 bg-gradient-to-br from-green-600 to-blue-600 rounded-lg relative overflow-hidden"
              style={{
                backgroundImage: team.team_logo ? `url(${team.team_logo})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-70" />
              
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-end justify-between">
                  <div className="flex items-end gap-4">
                    {/* Always show team logo circle - fallback to team initials if no logo */}
                    <div className="w-20 h-20 rounded-full border-4 border-white bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {team.team_logo ? (
                        <img 
                          src={team.team_logo} 
                          alt={`${team.team_name} logo`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-green-600 to-blue-600 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {team.team_name.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h1 className="text-3xl font-bold text-white mb-2">{team.team_name}</h1>
                      <div className="flex items-center gap-4 text-white/80">
                        {team.sport_type && (
                          <Badge className="bg-sports-green text-black font-semibold">
                            {team.sport_type}
                          </Badge>
                        )}
                        {team.age_group && (
                          <span className="text-sm">{team.age_group}</span>
                        )}
                        {team.division && (
                          <span className="text-sm">{team.division}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Follow Button in Header - Show for non-owners */}
                  {currentUser && !canManageTeam() && (
                    <TeamFollowButton
                      teamId={team.team_id}
                      userId={currentUserId}
                      userType={userType}
                      size="default"
                      showCount={true}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Featured Spotlight */}
          <FeaturedSpotlight 
            itemType="team" 
            itemId={team.team_id.toString()} 
            teamId={team.team_id}
            showPurchase={canManageTeam()}
          />

          {/* Social Media Embeds */}
          <SocialMediaEmbeds 
            userId={team.team_id.toString()}
            userType="team_owner"
            editable={canManageTeam()}
          />

          {/* Conditional rendering based on management permissions */}
          {canManageTeam() ? (
            <TeamManagementView 
              team={team}
              currentUserId={currentUserId}
            />
          ) : (
            <PublicTeamView 
              team={team}
              currentUserId={currentUserId}
            />
          )}
        </div>
      </main>
      
      <div className="block md:hidden">
        <MobileBottomNav />
      </div>
      <Footer />
    </div>
  );
};

export default TeamProfile;
